/**
 * Homepage weather card (top of the left column).
 *
 * Location resolution, first match wins:
 *   1. a location picked on this page just now
 *   2. signed-in user's saved weather_location (from /api/auth/me)
 *   3. localStorage 'nacho-weather-location'
 *   4. DEFAULT_LOCATION (Havana, AR — hardcoded coords; geocoding "Havana"
 *      by name returns Havana, Cuba)
 *
 * Picking a location saves to localStorage for everyone, and also to the
 * user's profile (PATCH /api/auth/me) when signed in. Guests never hit the DB.
 *
 * The mood icon is /images/weather/{mood}.png. Most of those images don't
 * exist yet, so onError swaps in the condition emoji in the same box.
 */

import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext.jsx';
import { getWeather, geocodeWeather } from '../api/weather.js';
import { updateProfile } from '../api/profile.js';
import './WeatherCard.css';


const DEFAULT_LOCATION = { name: 'Havana, AR', lat: 35.1111, lon: -93.5292 };
const STORAGE_KEY = 'nacho-weather-location';
const REFRESH_MS = 15 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;


function readStoredLocation() {
  try {
    const loc = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (loc && typeof loc.lat === 'number' && typeof loc.lon === 'number') return loc;
  } catch { /* private mode / bad JSON → ignore */ }
  return null;
}

function writeStoredLocation(loc) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
}

// "2026-10-31" → "Sat". Noon avoids the date sliding a day in UTC-negative zones.
function weekday(isoDate) {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
}


export default function WeatherCard() {
  const { user, loading: authLoading } = useAuth();

  const [picked, setPicked] = useState(null);
  const location = authLoading
    ? null
    : picked || user?.weather_location || readStoredLocation() || DEFAULT_LOCATION;
  const isDefault = location === DEFAULT_LOCATION;

  // ── weather fetch + 15-minute refresh ──
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(false);
  const lat = location?.lat;
  const lon = location?.lon;

  useEffect(() => {
    if (lat == null || lon == null) return undefined;
    let cancelled = false;

    function load() {
      getWeather(lat, lon)
        .then((data) => { if (!cancelled) { setWeather(data); setError(false); } })
        .catch(() => { if (!cancelled) setError(true); });
    }

    setWeather(null);
    setError(false);
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [lat, lon]);

  // ── change-location search ──
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    const q = query.trim();
    if (!editing || q.length < 2) { setResults([]); setSearchError(false); return undefined; }

    let cancelled = false;
    const id = setTimeout(() => {
      geocodeWeather(q)
        .then((data) => { if (!cancelled) { setResults(Array.isArray(data) ? data : []); setSearchError(false); } })
        .catch(() => { if (!cancelled) { setResults([]); setSearchError(true); } });
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(id); };
  }, [query, editing]);

  function closeSearch() {
    setEditing(false);
    setQuery('');
    setResults([]);
    setSearchError(false);
  }

  function choose(result) {
    const loc = { name: result.label.slice(0, 120), lat: result.lat, lon: result.lon };
    setPicked(loc);
    writeStoredLocation(loc);
    if (user) {
      // Best-effort: localStorage already has it if the profile save fails.
      updateProfile({ weather_location: loc }).catch(() => {});
    }
    closeSearch();
  }

  // ── mood icon (falls back to emoji until Tim draws the image) ──
  const mood = weather?.current.mood;
  const [iconFailed, setIconFailed] = useState(false);
  useEffect(() => { setIconFailed(false); }, [mood]);

  return (
    <div className="hr-card hr-weather">
      <div className="hr-wx-now">
        <div className="hr-wx-icon">
          {weather && (
            iconFailed
              ? <span className="hr-wx-icon-emoji" role="img" aria-label={weather.current.label}>
                  {weather.current.emoji}
                </span>
              : <img
                  src={`/images/weather/${mood}.png`}
                  alt={weather.current.label}
                  onError={() => setIconFailed(true)}
                />
          )}
        </div>

        <div className="hr-wx-info">
          {editing ? (
            <div className="hr-wx-search">
              <div className="hr-wx-search-row">
                <input
                  ref={inputRef}
                  className="hr-wx-search-input"
                  type="text"
                  placeholder="Search a city…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
                  aria-label="Search for a location"
                />
                <button type="button" className="hr-wx-search-close" onClick={closeSearch} aria-label="Cancel">
                  ✕
                </button>
              </div>
              {(results.length > 0 || searchError) && (
                <ul className="hr-wx-results">
                  {searchError && <li className="hr-wx-result-msg">Search is down. Try again in a bit.</li>}
                  {results.map((r) => (
                    <li key={`${r.lat},${r.lon}`}>
                      <button type="button" className="hr-wx-result" onClick={() => choose(r)}>
                        {r.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="hr-wx-loc"
              onClick={() => setEditing(true)}
              disabled={!location}
              title="Change location"
            >
              <span className="hr-wx-loc-name">{location ? location.name : ' '}</span>
              <span className="hr-wx-loc-edit" aria-hidden="true">✎</span>
            </button>
          )}

          {isDefault && !editing && <div className="hr-wx-why">Why Havana? Why not.</div>}

          {error ? (
            <div className="hr-wx-error">Weather’s out. Nacho is looking into it.</div>
          ) : weather ? (
            <>
              <div className="hr-wx-temp-row">
                <span className="hr-wx-temp">{weather.current.temp}°</span>
                <span className="hr-wx-label">{weather.current.label}</span>
              </div>
              <div className="hr-wx-detail">
                Feels like {weather.current.feels_like}° · {weather.current.wind_mph} mph
              </div>
              <div className="hr-wx-detail">
                H {weather.today.high}° · L {weather.today.low}°
              </div>
            </>
          ) : (
            <div className="hr-wx-placeholder" aria-hidden="true">
              <span /><span /><span />
            </div>
          )}
        </div>
      </div>

      {weather && !error && (
        <ul className="hr-wx-strip">
          {weather.forecast.map((d) => (
            <li className="hr-wx-day" key={d.date} title={d.label}>
              <span className="hr-wx-day-name">{weekday(d.date)}</span>
              <span className="hr-wx-day-emoji" role="img" aria-label={d.label}>{d.emoji}</span>
              <span className="hr-wx-day-temps">{d.high}°<span className="hr-wx-day-low">/{d.low}°</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
